"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { getRum } from "@/lib/rum/client";
import { toPageId } from "@/lib/rum/pageId";

/**
 * Record a RUM page view per navigation, using the route template as the
 * pageId (see toPageId).
 *
 * The client is configured with disableAutoPageView, so this is the only
 * source of page_view events — without it RUM would report no page views at
 * all.
 *
 * Uses next/navigation's usePathname (not next-intl's) deliberately: we want
 * the locale-prefixed path so toPageId can collapse it to /[locale] rather
 * than producing ten variants of every screen.
 */
export function useRumPageViews(): void {
  const pathname = usePathname();
  // Guards against React StrictMode's double effect invocation in dev, which
  // would otherwise double-count every page view. Compared on pathname, not
  // pageId, so navigating between two records of the same screen still counts
  // as two page views — that is what makes user-journey data meaningful.
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || pathname === lastPath.current) {
      return;
    }

    const rum = getRum();
    if (!rum) {
      return;
    }

    lastPath.current = pathname;

    try {
      rum.recordPageView({ pageId: toPageId(pathname) });
    } catch (error) {
      console.error("[rum] failed to record page view", error);
    }
  }, [pathname]);
}
